import ApplicationController from './application_controller'

/**
 * Progressive (blur) autosave for the Booking / Email widget checkout form.
 *
 * Each wired field persists its value to the existing lead as soon as it loses
 * focus, so nothing is lost if the customer abandons the widget before creating
 * an order (#7778). Only changed, client-valid values are sent, one field per
 * request. The order-creation / payment flow is untouched.
 *
 * On page hide (tab close / navigation) any changed field is flushed too, so the
 * field the customer was still typing in when they leave is not lost (AC7).
 *
 * If a field is edited again while its previous save is still in flight, the
 * latest value is queued and sent when the in-flight request finishes, so a fast
 * re-edit is never dropped.
 *
 * Wiring (see product_widgets/orders/_form.html.erb, widget_lead branch):
 * - root element: data-controller="progressive-save"
 *                 data-progressive-save-url-value="<%= product_widget_lead_detail_path(...) %>"
 *                 data-progressive-save-address-id-value="<%= lead.delivery_address&.id %>"
 * - each field:   data-action="blur->progressive-save#save"
 *
 * The field's own `name` (e.g. "widget_lead[delivery_address_attributes][email]")
 * is the exact param key the backend expects, so no per-field mapping is needed.
 */
export default class extends ApplicationController {
  static values = {
    url: String,
    addressId: Number
  }

  connect () {
    this.snapshot = new Map()
    this.inFlight = new Set()
    this.pending = new Map()
    this.#fields().forEach(field => {
      if (field.name) this.snapshot.set(field.name, field.value)
    })

    this.flushOnExit = () => this.#flushChanged()
    window.addEventListener('pagehide', this.flushOnExit)
  }

  disconnect () {
    window.removeEventListener('pagehide', this.flushOnExit)
  }

  #fields () {
    return this.element.querySelectorAll('[data-action~="blur->progressive-save#save"]')
  }

  save (event) {
    this.#saveField(event.currentTarget)
  }

  // Flush every changed field on exit — catches the field still focused when the
  // customer closes the tab (no blur fires). `keepalive` lets the request outlive
  // the page; unlike sendBeacon it can still send the CSRF header.
  #flushChanged () {
    // force: bypass the in-flight coalescing. On unload the earlier (non-keepalive)
    // request may be cancelled and #drainPending may never run, so the latest value
    // must fire its own keepalive request now rather than wait in the pending queue.
    //
    // Accepted limitation: if that earlier request already reached the server it
    // could still land after this one (no last-write-wins guarantee). Reaching it
    // requires re-editing the same field and closing the tab within one request's
    // round-trip; and the order-form submission re-validates before payment. A
    // server-side per-field sequence would be the only complete fix — deliberately
    // not added for this abandoned-lead autosave.
    this.#fields().forEach(field => this.#saveField(field, { keepalive: true, force: true }))
  }

  #saveField (field, { keepalive = false, force = false } = {}) {
    const name = field.name
    if (!name) return

    const value = field.value
    if (this.snapshot.get(name) === value) return
    if (field.willValidate && !field.checkValidity()) return

    const body = this.#buildBody(name, value)

    // A save for this field is already running: remember the latest value so it
    // is sent once the in-flight request completes, rather than dropped. On an
    // unload flush (force) we skip the queue and send the latest value directly.
    if (this.inFlight.has(name) && !force) {
      this.pending.set(name, { value, body, keepalive })
      return
    }

    this.#send(name, value, body, keepalive)
  }

  #buildBody (name, value) {
    const body = new FormData()
    body.append(name, value)

    if (name.includes('[delivery_address_attributes]') && this.hasAddressIdValue) {
      body.append('widget_lead[delivery_address_attributes][id]', this.addressIdValue)
    }

    // Persist the country code alongside the phone number it belongs to.
    if (name.endsWith('[phone]')) {
      const codeName = name.replace(/\[phone\]$/, '[phone_country_code]')
      const codeField = this.element.querySelector(`[name="${codeName}"]`)
      if (codeField) body.append(codeName, codeField.value)
    }

    return body
  }

  async #send (name, value, body, keepalive = false) {
    this.inFlight.add(name)

    try {
      const response = await fetch(this.urlValue, {
        method: 'PATCH',
        headers: {
          'X-CSRF-Token': this.#csrfToken(),
          Accept: 'application/json'
        },
        body,
        keepalive
      })
      // Only remember the value once it is safely persisted, so an invalid or
      // failed value is retried on the next blur.
      if (response.ok) this.snapshot.set(name, value)
    } catch {
      // Network hiccup: leave the snapshot untouched so the next blur retries.
    } finally {
      this.inFlight.delete(name)
      this.#drainPending(name)
    }
  }

  // Send the value queued while this field was saving, if it still differs from
  // what is now persisted.
  #drainPending (name) {
    const next = this.pending.get(name)
    if (!next) return

    this.pending.delete(name)
    if (this.snapshot.get(name) !== next.value) {
      this.#send(name, next.value, next.body, next.keepalive)
    }
  }

  #csrfToken () {
    return document.querySelector('meta[name="csrf-token"]')?.content || ''
  }
}
